'use client';
import { useEffect, useRef } from 'react';

interface UseEventListenerOptions extends EventListenerOptions {
  enabled?: boolean;
}

/**
 * Custom hook for adding event listeners to DOM elements or window
 * @param eventName - The event name to listen for
 * @param handler - The event handler function
 * @param element - The DOM element to attach the listener to (defaults to window)
 * @param options - Additional event listener options
 */
export const useEventListener = <T extends Event = Event>(
  eventName: string,
  handler: (event: T) => void,
  element?: Element | Window | Document | null,
  options: UseEventListenerOptions = {},
) => {
  const { enabled = true, ...eventOptions } = options;

  // Store the handler in a ref to avoid re-adding listeners on every render
  const savedHandler = useRef<(event: T) => void>(handler);

  // Update the saved handler when it changes
  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    // Determine the target element with SSR check
    const targetElement = element || (typeof window !== 'undefined' ? window : null);

    if (!enabled || !targetElement) return;

    // Create event listener that calls the saved handler
    const eventListener = (event: Event) => {
      savedHandler.current(event as T);
    };

    // Add the event listener
    targetElement.addEventListener(eventName, eventListener, eventOptions);

    // Cleanup function to remove the event listener
    return () => {
      targetElement.removeEventListener(eventName, eventListener, eventOptions);
    };
  }, [eventName, element, enabled, eventOptions]);
};

/**
 * Custom hook for adding multiple event listeners at once
 * @param listeners - Array of event listener configurations
 */
export const useMultipleEventListeners = (
  listeners: Array<{
    eventName: string;
    handler: (event: Event) => void;
    element?: Element | Window | Document | null;
    options?: UseEventListenerOptions;
  }>,
) => {
  useEffect(() => {
    const cleanupFunctions: (() => void)[] = [];

    listeners.forEach(({ eventName, handler, element, options = {} }) => {
      const { enabled = true, ...eventOptions } = options;
      const targetElement = element || (typeof window !== 'undefined' ? window : null);

      if (!enabled || !targetElement) return;

      const eventListener = (event: Event) => {
        handler(event);
      };

      targetElement.addEventListener(eventName, eventListener, eventOptions);

      cleanupFunctions.push(() => {
        targetElement.removeEventListener(eventName, eventListener, eventOptions);
      });
    });

    // Return cleanup function for all listeners
    return () => {
      cleanupFunctions.forEach((cleanup) => cleanup());
    };
  }, [listeners]);
};
